import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import useLoader from "../../../hooks/useLoader";
import {
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import DashboardLayout from "../../../layouts/Dashboard";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { mapPlanData } from "../../../functions/viewDataMap";
import { serializePlanIndicesToIndicatorGroups } from "../../../functions/planIndicatorsSerialization";
import { buildPlanRollupFromIndices } from "../../../functions/planRollup";
import { useSelector } from "react-redux";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  departmentCRUD,
  diseaseIndexCRUD,
  diseaseIndexServiceCRUD,
  planCRUD,
  planDocumentCRUD,
  serviceGroupCRUD,
  serviceMapCRUD,
  servicePriceListCRUD,
  serviceSubGroupCRUD,
  serviceCRUD,
} from "../../../http";
import useAlert from "../../../hooks/useAlert";
import { PLAN_DOCUMENT_ROUTE, PLAN_LIST_ROUTE } from "../../../routes";
import Stack from "@mui/material/Stack";
import { Table } from "../../../components/PDFGenerator/Table";
import AnObjectField from "../../../components/Relations/AnObjectField";
import RestAutoComplete from "../../../components/Autocompletes/RestAutoComplete";

/** Период документа плана → Date для полей объекта. */
const periodFromPlanDocument = (doc) => {
  const raw = doc?.month ?? doc?.Month;
  if (raw != null && raw !== "") {
    let iso = raw;
    if (
      typeof raw === "string" &&
      /^\d{4}-\d{2}-\d{2}T/.test(raw) &&
      !/(Z|[+-]\d{2}:?\d{2})$/i.test(raw)
    ) {
      iso = `${raw}Z`;
    }
    const asDate = new Date(iso);
    if (!Number.isNaN(asDate.getTime())) {
      return new Date(asDate.getFullYear(), asDate.getMonth(), 1);
    }
  }
  const y = Number(doc?.year);
  if (Number.isFinite(y) && y >= 2000 && y <= 2100) {
    return new Date(y, 0, 1);
  }
  return new Date();
};

const PLAN_RELATION_KEYS = [
  "department",
  "filial",
  "filialDivision",
  "division",
  "service",
  "group",
  "subGroup",
  "kind",
  "unit",
  "priceList",
  "contract",
  "state",
  "createdBy",
  "indices",
  "targetObject",
  "purposeOfSelection",
  "section",
  "subSection",
  "anObjectSection",
  "anObjectSubSection",
  "indicators",
  "Indicators",
];

const parseNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const createEmptyPlanIndex = () => ({
  id: null,
  indexId: "",
  indexName: "",
  serviceId: null,
  departmentId: null,
  departmentName: null,
  groupId: null,
  groupName: null,
  subGroupId: null,
  plannedPlan: 1,
  plannedResearch: 0,
  plannedSum: 0,
  donePlan: 0,
  doneResearch: 0,
  doneSum: 0,
  _isDeleted: false,
  _manualSum: false,
});

const createIndicatorGroup = () => ({
  key: `grp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  departmentId: null,
  departmentName: null,
  groupId: null,
  groupName: null,
  indicators: [],
  loading: false,
});

const planIndexSectionKey = (row) => {
  const hasDept =
    row.departmentId != null && String(row.departmentId).trim() !== "";
  const hasGroup = row.groupId != null && String(row.groupId).trim() !== "";
  if (!hasDept && !hasGroup) return "_none";
  const d = hasDept ? String(row.departmentId) : "_d";
  const g = hasGroup ? String(row.groupId) : "_g";
  return `${d}|${g}`;
};

const sectionLabelForPlanIndexRow = (row) => {
  if (planIndexSectionKey(row) === "_none") return "Без лаборатории";
  const dept =
    row.departmentName || row.department?.name || "Лаборатория";
  const grp =
    row.groupName || row.group?.name || row.serviceGroup?.name || null;
  return grp ? `${dept} — ${grp}` : dept;
};

const buildPlanIndexGroups = (planIndices) => {
  const groups = [];
  const indexByKey = new Map();
  for (let fullIdx = 0; fullIdx < planIndices.length; fullIdx++) {
    const row = planIndices[fullIdx];
    if (row._isDeleted) continue;
    const key = planIndexSectionKey(row);
    if (!indexByKey.has(key)) {
      indexByKey.set(key, groups.length);
      groups.push({
        groupKey: key,
        departmentLabel: sectionLabelForPlanIndexRow(row),
        entries: [],
      });
    }
    groups[indexByKey.get(key)].entries.push({ row, fullIdx });
  }
  return groups;
};

const SYNTHETIC_INDEX_OFFSET = 900000000;

const normalizeIndicatorName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-zа-я0-9]+/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const pickRows = (response) => {
  if (Array.isArray(response?.result)) return response.result;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};

const rowMatchesPlanGroupIndicator = (row, group, item) => {
  const rDept =
    row.departmentId != null && row.departmentId !== ""
      ? Number(row.departmentId)
      : null;
  const gDept =
    group.departmentId != null && group.departmentId !== ""
      ? Number(group.departmentId)
      : null;
  if (rDept !== gDept) return false;
  const rG =
    row.groupId != null && row.groupId !== "" ? Number(row.groupId) : null;
  const gG =
    group.groupId != null && group.groupId !== "" ? Number(group.groupId) : null;
  if (rG !== gG) return false;
  if (Number(item.id) >= SYNTHETIC_INDEX_OFFSET) {
    return (
      normalizeIndicatorName(row.indexName) === normalizeIndicatorName(item.name)
    );
  }
  return Number(row.indexId) === Number(item.id);
};

const PlanDetailForm = () => {
  const history = useHistory();
  const alert = useAlert(false, 1);
  const locationState = useLocation().state || {};
  const { review } = locationState;
  const planDocumentId =
    locationState.planDocumentId != null
      ? Number(locationState.planDocumentId)
      : null;
  const backRoute =
    planDocumentId != null
      ? PLAN_DOCUMENT_ROUTE(planDocumentId)
      : PLAN_LIST_ROUTE;
  const [fields, setFields] = useState({});
  const [planIndices, setPlanIndices] = useState([]);
  const [diseaseIndices, setDiseaseIndices] = useState([]);
  const [indexPriceMap, setIndexPriceMap] = useState({});
  const [indexNameIdMap, setIndexNameIdMap] = useState({});
  const [indicatorGroups, setIndicatorGroups] = useState([createIndicatorGroup()]);
  const [selectedObjectSectionName, setSelectedObjectSectionName] = useState("");
  const authorizedEmployee = useSelector((state) => state.employee.authorizedEmployee);
  const { loading, start, stop } = useLoader(false);
  const { loading: loadingSubmit, start: startSubmit, stop: stopSubmit } = useLoader(false);

  const submitPlanData = useMemo(() => mapPlanData(fields), [fields]);

  const planIndexTableGroups = useMemo(
    () => buildPlanIndexGroups(planIndices),
    [planIndices]
  );

  const fetch = useCallback(async () => {
    try {
      start();
      let docDefaults = {
        month: new Date(),
        year: new Date(),
        filialId: null,
        filialDivisionId: null,
        contractId: null,
        districtCity: "",
      };
      if (planDocumentId != null && Number.isFinite(planDocumentId)) {
        try {
          const doc = await planDocumentCRUD.get(planDocumentId);
          const period = periodFromPlanDocument(doc);
          docDefaults = {
            month: period,
            year: period,
            filialId: doc?.filialId ?? doc?.filial?.id ?? null,
            filialDivisionId:
              doc?.filialDivisionId ?? doc?.filialDivision?.id ?? null,
            contractId: doc?.contractId ?? doc?.contract?.id ?? null,
            districtCity: doc?.districtCity ?? "",
          };
        } catch (error) {
          console.warn("Не удалось загрузить документ плана:", error);
        }
      }
      setFields({
        ...docDefaults,
        amount: 0,
        sum: 0,
        inspectedEntityName: "",
        individualOrBusinessId: "",
        inspectedObjectName: "",
        location: "",
        month1: 0,
        month2: 0,
        month3: 0,
        month4: 0,
        month5: 0,
        month6: 0,
        month7: 0,
        month8: 0,
        month9: 0,
        month10: 0,
        month11: 0,
        month12: 0,
        targetObject: null,
        purposeId: null,
        sectionId: null,
        subSectionId: null,
      });
      setSelectedObjectSectionName("");
    } finally {
      stop();
    }
  }, [planDocumentId, start, stop]);

  const loadDiseaseIndices = useCallback(async () => {
    try {
      const data = await diseaseIndexCRUD.search({
        paging: { skip: 0, take: null, returnCount: true },
        sort: { id: { operator: "asc", ordinal: 0 } },
      });
      setDiseaseIndices(data?.result || []);
    } catch (error) {
      alert.show(0, error);
    }
  }, [alert]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    loadDiseaseIndices();
  }, [loadDiseaseIndices]);

  const submit = useCallback(async () => {
    try {
      startSubmit();
      if (planDocumentId == null || !Number.isFinite(planDocumentId)) {
        throw new Error(
          "Объект можно создать только внутри документа плана. Откройте план и нажмите «Добавить объект»."
        );
      }
      const rollup = buildPlanRollupFromIndices(planIndices, fields);
      const payload = {
        ...submitPlanData,
        planDocumentId,
        ...rollup,
        sum: rollup.sum || parseNumber(fields.sum),
        month: fields.year ? new Date(fields.year).toISOString() : fields.month,
        createdById: fields.createdById || authorizedEmployee?.id || null,
      };

      PLAN_RELATION_KEYS.forEach((key) => {
        delete payload[key];
      });

      payload.indicators = JSON.stringify(
        serializePlanIndicesToIndicatorGroups(planIndices)
      );

      await planCRUD.create(payload);
      alert.show(1, "Объект плана создан");
      history.push(backRoute);
    } catch (error) {
      alert.show(0, error);
    } finally {
      stopSubmit();
    }
  }, [
    alert,
    authorizedEmployee?.id,
    backRoute,
    fields.month,
    fields.sum,
    fields.year,
    history,
    planDocumentId,
    planIndices,
    startSubmit,
    stopSubmit,
    submitPlanData,
  ]);

  const getPriceByServiceId = useCallback(async (serviceId) => {
    const numericServiceId = Number(serviceId);
    if (!Number.isFinite(numericServiceId) || numericServiceId <= 0) return 0;
    try {
      const searchResult = await servicePriceListCRUD.search({
        paging: { pageNumber: 1, pageSize: 1, returnCount: false },
        filter: {
          serviceId: { operand1: String(numericServiceId), operator: "equals" },
        },
      });
      const searchRows = pickRows(searchResult);
      if (searchRows.length > 0) {
        const price = Number(searchRows[0]?.price);
        if (Number.isFinite(price) && price > 0) return price;
      }

      const serviceData = await serviceCRUD.get(numericServiceId);
      const fallbackPrice = [serviceData?.price, serviceData?.prices?.[0]?.price]
        .map((value) => Number(value))
        .find((value) => Number.isFinite(value) && value > 0);
      return fallbackPrice || 0;
    } catch (error) {
      console.warn("Ошибка получения цены по serviceId:", error);
      return 0;
    }
  }, []);

  const getServicePrice = useCallback(async (indexId, preferredServiceId = null) => {
    // Если есть цена в Plan, используем её
    if (fields.price && Number(fields.price) > 0) {
      return Number(fields.price);
    }

    // Для выбранного показателя приоритетнее его serviceId из тарификатора
    const directServicePrice = await getPriceByServiceId(preferredServiceId);
    if (directServicePrice > 0) {
      return directServicePrice;
    }

    // Если есть serviceId, пытаемся получить цену из servicePriceList
    if (fields.serviceId) {
      try {
        // Сначала пробуем найти по priceListId, если он есть
        if (fields.priceListId) {
          const priceListData = await servicePriceListCRUD.get(fields.priceListId);
          if (priceListData?.price && Number(priceListData.price) > 0) {
            return Number(priceListData.price);
          }
        }

        // Если не нашли по priceListId, ищем по serviceId
        const searchResult = await servicePriceListCRUD.search({
          paging: { pageNumber: 1, pageSize: 1, returnCount: false },
          filter: {
            serviceId: { operand1: String(fields.serviceId), operator: "equals" },
          },
        });

        if (searchResult?.result?.length > 0) {
          const price = searchResult.result[0]?.price;
          if (price && Number(price) > 0) {
            return Number(price);
          }
        }

        // Если не нашли в servicePriceList, пробуем получить из service
        const serviceData = await serviceCRUD.get(fields.serviceId);
        if (serviceData?.price && Number(serviceData.price) > 0) {
          return Number(serviceData.price);
        }
      } catch (error) {
        console.warn("Ошибка получения цены услуги:", error);
      }
    }

    // Если в плане услуга не выбрана, пробуем взять цену по индикатору (DiseaseIndex -> services)
    if (indexId != null && indexId !== "") {
      const numericIndexId = Number(indexId);
      if (Number.isFinite(numericIndexId) && numericIndexId > 0) {
        const cached = indexPriceMap[numericIndexId];
        if (Number.isFinite(cached) && cached > 0) {
          return cached;
        }

        try {
          const diseaseIndex = await diseaseIndexCRUD.get(numericIndexId);
          const services = Array.isArray(diseaseIndex?.services) ? diseaseIndex.services : [];
          const firstService = services[0] || null;
          const directPrice = [
            firstService?.price,
            firstService?.service?.price,
            firstService?.servicePrice,
            firstService?.service?.prices?.[0]?.price,
          ]
            .map((value) => Number(value))
            .find((value) => Number.isFinite(value) && value > 0);

          if (Number.isFinite(directPrice) && directPrice > 0) {
            setIndexPriceMap((prev) => ({ ...prev, [numericIndexId]: directPrice }));
            return directPrice;
          }

          // Часто бэкенд возвращает только serviceId без раскрытого service -> догружаем услугу
          let mappedServiceId = Number(
            firstService?.serviceId ?? firstService?.service?.id ?? NaN
          );
          
          // Фолбэк: ищем service по code === serviceCode из diseaseIndex
          if ((!Number.isFinite(mappedServiceId) || mappedServiceId <= 0) && firstService?.serviceCode) {
            try {
              const byCodeResp = await serviceCRUD.search({
                paging: { skip: 0, take: 1, returnCount: false },
                filter: {
                  code: { operand1: String(firstService.serviceCode), operator: "equals" },
                },
              });
              const byCodeRows = pickRows(byCodeResp);
              if (byCodeRows.length > 0 && Number(byCodeRows[0].id) > 0) {
                mappedServiceId = Number(byCodeRows[0].id);
              }
            } catch (err) {
              console.warn("Не удалось найти service по serviceCode:", err);
            }
          }
          
          // Фолбэк 2: если diseaseIndex.services пуст, ищем через diseaseIndexServiceCRUD
          if (!Number.isFinite(mappedServiceId) || mappedServiceId <= 0) {
            try {
              const disResp = await diseaseIndexServiceCRUD.search({
                paging: { skip: 0, take: 10, returnCount: false },
                filter: {
                  indexId: { operand1: String(numericIndexId), operator: "equals" },
                },
              });
              const disRows = pickRows(disResp);
              const foundSid = disRows
                .map((item) => Number(item.serviceId))
                .find((v) => Number.isFinite(v) && v > 0);
              if (foundSid > 0) {
                mappedServiceId = foundSid;
              } else {
                const foundCode = disRows
                  .map((item) => item.serviceCode)
                  .find((v) => v != null && String(v).trim() !== "");
                if (foundCode) {
                  const byCodeResp2 = await serviceCRUD.search({
                    paging: { skip: 0, take: 1, returnCount: false },
                    filter: {
                      code: { operand1: String(foundCode), operator: "equals" },
                    },
                  });
                  const byCodeRows2 = pickRows(byCodeResp2);
                  if (byCodeRows2.length > 0 && Number(byCodeRows2[0].id) > 0) {
                    mappedServiceId = Number(byCodeRows2[0].id);
                  }
                }
              }
            } catch (err) {
              console.warn("getServicePrice: ошибка diseaseIndexServiceCRUD:", err);
            }
          }
          
          if (Number.isFinite(mappedServiceId) && mappedServiceId > 0) {
            const priceByServiceId = await getPriceByServiceId(mappedServiceId);
            if (priceByServiceId > 0) {
              setIndexPriceMap((prev) => ({ ...prev, [numericIndexId]: priceByServiceId }));
              return priceByServiceId;
            }
            const serviceData = await serviceCRUD.get(mappedServiceId);
            const servicePrice = [serviceData?.price, serviceData?.prices?.[0]?.price]
              .map((value) => Number(value))
              .find((value) => Number.isFinite(value) && value > 0);
            if (Number.isFinite(servicePrice) && servicePrice > 0) {
              setIndexPriceMap((prev) => ({ ...prev, [numericIndexId]: servicePrice }));
              return servicePrice;
            }
          }
        } catch (error) {
          console.warn("Ошибка получения цены по индикатору:", error);
        }
      }
    }

    return 0;
  }, [fields.price, fields.serviceId, fields.priceListId, getPriceByServiceId, indexPriceMap]);

  const resolveRowIndexId = useCallback(
    async (row) => {
      const directId = Number(row?.indexId);
      if (Number.isFinite(directId) && directId > 0) {
        return directId;
      }

      const normalizedRowName = normalizeIndicatorName(row?.indexName);
      if (!normalizedRowName) return null;
      const cachedId = Number(indexNameIdMap[normalizedRowName]);
      if (Number.isFinite(cachedId) && cachedId > 0) {
        return cachedId;
      }

      const pool = (diseaseIndices || []).map((item) => ({
        id: Number(item.id),
        normalized: normalizeIndicatorName(item.name),
      }));

      const exact = pool.find((item) => item.normalized === normalizedRowName);
      if (exact?.id) {
        setIndexNameIdMap((prev) => ({ ...prev, [normalizedRowName]: Number(exact.id) }));
        return exact.id;
      }

      const fuzzy = pool.find(
        (item) =>
          item.normalized.includes(normalizedRowName) ||
          normalizedRowName.includes(item.normalized)
      );
      if (fuzzy?.id) {
        setIndexNameIdMap((prev) => ({ ...prev, [normalizedRowName]: Number(fuzzy.id) }));
        return fuzzy.id;
      }

      // Фолбэк: ищем на сервере по имени индикатора
      try {
        const byNameResponse = await diseaseIndexCRUD.search({
          paging: { skip: 0, take: 50, returnCount: false },
          filter: {
            name: {
              operand1: String(row?.indexName || "").trim(),
              operator: "like",
            },
          },
          sort: { id: { operator: "asc", ordinal: 0 } },
        });
        const candidates = pickRows(byNameResponse);
        const normalizedCandidates = candidates.map((item) => ({
          id: Number(item.id),
          normalized: normalizeIndicatorName(item.name),
        }));
        const exactRemote =
          normalizedCandidates.find((item) => item.normalized === normalizedRowName) ||
          normalizedCandidates.find(
            (item) =>
              item.normalized.includes(normalizedRowName) ||
              normalizedRowName.includes(item.normalized)
          );
        if (exactRemote?.id) {
          setIndexNameIdMap((prev) => ({ ...prev, [normalizedRowName]: Number(exactRemote.id) }));
          return Number(exactRemote.id);
        }
      } catch (error) {
        console.warn("Не удалось найти indexId по имени индикатора:", error);
      }

      return null;
    },
    [diseaseIndices, indexNameIdMap]
  );

  const updatePlanIndexField = useCallback(async (idx, key, value) => {
    const numValue = parseNumber(value);
    
    // Если изменился plannedResearch, автоматически рассчитываем plannedSum
    if (key === "plannedResearch") {
      const row = planIndices[idx];
      if (!row) return;
      
      console.log("=== [PlanDetail] updatePlanIndexField ===");
      console.log("Все строки planIndices:", JSON.stringify(planIndices, null, 2));
      console.log("Текущая строка idx:", idx);
      console.log("Индикатор:", row?.indexName, "| indexId:", row?.indexId, "| serviceId из строки:", row?.serviceId);
      
      let price = 0;
      let resolvedServiceId = row?.serviceId;
      
      // Если serviceId нет в строке, пробуем найти его по indexId
      if (!resolvedServiceId || Number(resolvedServiceId) <= 0) {
        const resolvedIndexId = await resolveRowIndexId(row);
        console.log("resolvedIndexId:", resolvedIndexId);
        if (resolvedIndexId && Number(resolvedIndexId) > 0) {
          try {
            const diseaseIndex = await diseaseIndexCRUD.get(resolvedIndexId);
            const services = Array.isArray(diseaseIndex?.services) ? diseaseIndex.services : [];
            console.log("diseaseIndex.services:", JSON.stringify(services));
            const firstService = services[0] || null;
            resolvedServiceId = Number(
              firstService?.serviceId ?? firstService?.service?.id ?? 0
            );
            console.log("serviceId из diseaseIndex.services:", resolvedServiceId, "| serviceCode:", firstService?.serviceCode);
            
            // Фолбэк: ищем service по code === diseaseIndex.serviceCode
            if ((!resolvedServiceId || resolvedServiceId <= 0) && firstService?.serviceCode) {
              try {
                console.log("Ищем service по code:", firstService.serviceCode);
                const byCodeResp = await serviceCRUD.search({
                  paging: { skip: 0, take: 1, returnCount: false },
                  filter: {
                    code: { operand1: String(firstService.serviceCode), operator: "equals" },
                  },
                });
                const byCodeRows = pickRows(byCodeResp);
                console.log("Результат поиска service по code:", JSON.stringify(byCodeRows));
                if (byCodeRows.length > 0 && Number(byCodeRows[0].id) > 0) {
                  resolvedServiceId = Number(byCodeRows[0].id);
                  console.log("Найден serviceId по code:", resolvedServiceId);
                }
              } catch (err) {
                console.warn("Не удалось найти service по serviceCode:", err);
              }
            }
            
            // Фолбэк 2: если diseaseIndex.services пуст, ищем через diseaseIndexServiceCRUD
            if (!resolvedServiceId || resolvedServiceId <= 0) {
              try {
                console.log("diseaseIndex.services пуст, ищем через diseaseIndexServiceCRUD по indexId:", resolvedIndexId);
                const disResp = await diseaseIndexServiceCRUD.search({
                  paging: { skip: 0, take: 10, returnCount: false },
                  filter: {
                    indexId: { operand1: String(resolvedIndexId), operator: "equals" },
                  },
                });
                const disRows = pickRows(disResp);
                console.log("diseaseIndexServiceCRUD результат:", JSON.stringify(disRows));
                const foundServiceId = disRows
                  .map((item) => Number(item.serviceId))
                  .find((v) => Number.isFinite(v) && v > 0);
                if (foundServiceId > 0) {
                  resolvedServiceId = foundServiceId;
                  console.log("Найден serviceId через diseaseIndexServiceCRUD:", resolvedServiceId);
                }
                
                // Если и тут пусто, пробуем по serviceCode из diseaseIndexServiceCRUD
                if (!resolvedServiceId || resolvedServiceId <= 0) {
                  const foundServiceCode = disRows
                    .map((item) => item.serviceCode)
                    .find((v) => v != null && String(v).trim() !== "");
                  if (foundServiceCode) {
                    console.log("Ищем service по code из diseaseIndexService:", foundServiceCode);
                    const byCodeResp2 = await serviceCRUD.search({
                      paging: { skip: 0, take: 1, returnCount: false },
                      filter: {
                        code: { operand1: String(foundServiceCode), operator: "equals" },
                      },
                    });
                    const byCodeRows2 = pickRows(byCodeResp2);
                    if (byCodeRows2.length > 0 && Number(byCodeRows2[0].id) > 0) {
                      resolvedServiceId = Number(byCodeRows2[0].id);
                      console.log("Найден serviceId по code:", resolvedServiceId);
                    }
                  }
                }
              } catch (err) {
                console.warn("Ошибка поиска через diseaseIndexServiceCRUD:", err);
              }
            }
            
            // Сохраняем найденный serviceId в строку для будущих расчётов
            if (resolvedServiceId > 0) {
              console.log("Сохраняем serviceId в строку:", resolvedServiceId);
              setPlanIndices((prev) =>
                prev.map((item, itemIdx) =>
                  itemIdx === idx ? { ...item, serviceId: resolvedServiceId } : item
                )
              );
            }
          } catch (error) {
            console.warn("Не удалось найти serviceId по indexId:", error);
          }
        }
      }
      
      // Пробуем получить цену по serviceId
      if (resolvedServiceId && Number(resolvedServiceId) > 0) {
        console.log("Получаем цену по serviceId:", resolvedServiceId);
        price = await getPriceByServiceId(resolvedServiceId);
        console.log("Цена по serviceId:", price);
      }
      
      // Если не нашли, пробуем по indexId через getServicePrice
      if (price <= 0) {
        const resolvedIndexId = await resolveRowIndexId(row);
        console.log("Фолбэк getServicePrice, indexId:", resolvedIndexId);
        price = await getServicePrice(resolvedIndexId, resolvedServiceId);
        console.log("Цена из getServicePrice:", price);
      }
      
      const hasPrice = Number(price) > 0;
      const calculatedSum = hasPrice ? numValue * price : 0;
      console.log("Итого: кол-во =", numValue, "× цена =", price, "= сумма", calculatedSum, "| ручной ввод:", !hasPrice);
      console.log("=== конец расчёта ===");
      
      setPlanIndices((prev) =>
        prev.map((item, itemIdx) =>
          itemIdx === idx
            ? {
                ...item,
                [key]: numValue,
                plannedSum: hasPrice ? calculatedSum : item.plannedSum,
                _manualSum: !hasPrice,
              }
            : item
        )
      );
    } else {
      setPlanIndices((prev) =>
        prev.map((item, itemIdx) =>
          itemIdx === idx ? { ...item, [key]: numValue } : item
        )
      );
    }
  }, [getServicePrice, getPriceByServiceId, planIndices, resolveRowIndexId]);

  const addIndicatorsToPlan = useCallback((items = [], meta = {}) => {
    const {
      departmentId = null,
      departmentName = null,
      groupId = null,
      groupName = null,
    } = meta;
    const deptPart =
      departmentId != null && String(departmentId).trim() !== ""
        ? String(departmentId)
        : "_";
    const grpPart =
      groupId != null && String(groupId).trim() !== ""
        ? String(groupId)
        : "_";
    setPlanIndices((prev) => {
      const existing = new Set(
        prev
          .filter((row) => !row._isDeleted)
          .map((row) => {
            const d =
              row.departmentId != null && String(row.departmentId).trim() !== ""
                ? String(row.departmentId)
                : "_";
            const g =
              row.groupId != null && String(row.groupId).trim() !== ""
                ? String(row.groupId)
                : "_";
            return row.indexId === "" || row.indexId == null
              ? `name:${String(row.indexName || "").toLowerCase()}|dept:${d}|grp:${g}`
              : `id:${Number(row.indexId)}|dept:${d}|grp:${g}`;
          })
      );
      const existingInSection = prev.find((row) => {
        if (row._isDeleted) return false;
        const d =
          row.departmentId != null && String(row.departmentId).trim() !== ""
            ? String(row.departmentId)
            : "_";
        const g =
          row.groupId != null && String(row.groupId).trim() !== ""
            ? String(row.groupId)
            : "_";
        return d === deptPart && g === grpPart;
      });
      const sectionSampleCount =
        existingInSection?.plannedPlan != null && existingInSection.plannedPlan !== ""
          ? Number(existingInSection.plannedPlan)
          : 1;

      const toAdd = items
        .filter((item) => {
          const itemKey =
            Number(item.id) >= SYNTHETIC_INDEX_OFFSET
              ? `name:${String(item.name || "").toLowerCase()}|dept:${deptPart}|grp:${grpPart}`
              : `id:${Number(item.id)}|dept:${deptPart}|grp:${grpPart}`;
          return !existing.has(itemKey);
        })
        .map((item) => ({
          ...createEmptyPlanIndex(),
          plannedPlan: Number.isFinite(sectionSampleCount) ? sectionSampleCount : 1,
          indexId: Number(item.id) >= SYNTHETIC_INDEX_OFFSET ? "" : Number(item.id),
          indexName: item.name || `Индекс ${item.id}`,
          serviceId: Number(item.serviceId) > 0 ? Number(item.serviceId) : null,
          subGroupId: Number(item.subGroupId) > 0 ? Number(item.subGroupId) : null,
          departmentId:
            departmentId != null && String(departmentId).trim() !== ""
              ? Number(departmentId)
              : null,
          departmentName: departmentName || null,
          groupId:
            groupId != null && String(groupId).trim() !== ""
              ? Number(groupId)
              : null,
          groupName: groupName || null,
        }));
      return [...prev, ...toAdd];
    });
  }, []);

  const removePlanRowForGroupIndicator = useCallback((group, item) => {
    setPlanIndices((prev) =>
      prev.filter((row) => !rowMatchesPlanGroupIndicator(row, group, item))
    );
  }, []);

  const removePlanRowsForGroupIndicatorList = useCallback((group, indicatorList) => {
    setPlanIndices((prev) =>
      prev.filter(
        (row) =>
          !indicatorList.some((ind) =>
            rowMatchesPlanGroupIndicator(row, group, ind)
          )
      )
    );
  }, []);

  const updateIndicatorGroup = useCallback((groupKey, patch) => {
    setIndicatorGroups((prev) =>
      prev.map((group) =>
        group.key === groupKey
          ? { ...group, ...(typeof patch === "function" ? patch(group) : patch) }
          : group
      )
    );
  }, []);

  const addIndicatorGroup = () => {
    setIndicatorGroups((prev) => [...prev, createIndicatorGroup()]);
  };

  const removeIndicatorGroup = (groupKey) => {
    setIndicatorGroups((prev) => prev.filter((group) => group.key !== groupKey));
  };

  const loadGroupIndicators = useCallback(
    async (groupKey) => {
      const currentGroup = indicatorGroups.find((group) => group.key === groupKey);
      if (!currentGroup?.departmentId || !currentGroup?.groupId) {
        alert.show(0, "Выберите лабораторию и группу");
        return;
      }

      try {
        updateIndicatorGroup(groupKey, { loading: true, indicators: [] });

        const serviceMapsResponse = await serviceMapCRUD.search({
          paging: { skip: 0, take: 10000, returnCount: true },
          filter: {
            departmentId: {
              operand1: String(currentGroup.departmentId),
              operator: "equals",
            },
            groupId: {
              operand1: String(currentGroup.groupId),
              operator: "equals",
            },
          },
        });
        const serviceMapsPrimary = pickRows(serviceMapsResponse);
        const serviceMapsFallbackResponse =
          serviceMapsPrimary.length > 0
            ? serviceMapsResponse
            : await serviceMapCRUD.search({
                paging: { skip: 0, take: 10000, returnCount: true },
                filter: {
                  groupId: {
                    operand1: String(currentGroup.groupId),
                    operator: "equals",
                  },
                },
              });
        const serviceMapsRows = pickRows(serviceMapsFallbackResponse);

        const serviceIds = Array.from(
          new Set(
            serviceMapsRows
              .map((item) => Number(item.serviceId ?? item.service?.id))
              .filter((value) => Number.isFinite(value) && value > 0)
          )
        );

        const mappings = serviceIds.length
          ? await Promise.all(
              serviceIds.map(async (serviceId) => {
                const response = await diseaseIndexServiceCRUD.search({
                  paging: { skip: 0, take: 10000, returnCount: false },
                  filter: {
                    serviceId: {
                      operand1: String(serviceId),
                      operator: "equals",
                    },
                  },
                });
                return pickRows(response);
              })
            )
          : [];

        const flatMappings = mappings.flat();
        const indexToServiceMap = new Map();
        flatMappings.forEach((item) => {
          const idx = Number(item.indexId);
          const sid = Number(item.serviceId);
          if (!Number.isFinite(idx) || idx <= 0 || !Number.isFinite(sid) || sid <= 0) return;
          if (!indexToServiceMap.has(idx)) {
            indexToServiceMap.set(idx, sid);
          }
        });

        const indexIds = Array.from(
          new Set(
            flatMappings
              .map((item) => Number(item.indexId))
              .filter((value) => Number.isFinite(value) && value > 0)
          )
        );

        const knownById = new Map(
          (diseaseIndices || []).map((item) => [Number(item.id), item.name || `Индекс ${item.id}`])
        );
        const diseasePool = (diseaseIndices || []).map((item) => ({
          id: Number(item.id),
          name: item.name || `Индекс ${item.id}`,
          normalized: normalizeIndicatorName(item.name),
        }));

        const missingIds = indexIds.filter((indexId) => !knownById.has(Number(indexId)));
        if (missingIds.length) {
          const missingItems = await Promise.all(
            missingIds.map(async (indexId) => {
              try {
                const item = await diseaseIndexCRUD.get(indexId);
                return { id: Number(indexId), name: item?.name || `Индекс ${indexId}` };
              } catch (error) {
                return { id: Number(indexId), name: `Индекс ${indexId}` };
              }
            })
          );
          missingItems.forEach((item) => knownById.set(Number(item.id), item.name));
        }

        let indicators = indexIds
          .map((indexId) => ({
            id: Number(indexId),
            name: knownById.get(Number(indexId)),
            serviceId: indexToServiceMap.get(Number(indexId)) || null,
          }))
          .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ru"));

        if (!indicators.length) {
          // Фолбэк как в предписании: через serviceMap -> subGroup -> название показателя
          const subGroupIds = Array.from(
            new Set(
              serviceMapsRows
                .map((item) => Number(item.subGroupId))
                .filter((value) => Number.isFinite(value) && value > 0)
            )
          );
          if (subGroupIds.length) {
            const subGroupsResponse = await serviceSubGroupCRUD.search({
              paging: { skip: 0, take: 10000, returnCount: false },
            });
            const subGroupsById = new Map(
              pickRows(subGroupsResponse).map((item) => [Number(item.id), item.name || ""])
            );
            indicators = subGroupIds
              .map((subGroupId) => ({
                subGroupId: Number(subGroupId),
                name: String(subGroupsById.get(Number(subGroupId)) || "").trim(),
              }))
              .filter(Boolean)
              .map((item) => {
                const normalized = normalizeIndicatorName(item.name);
                const matched =
                  diseasePool.find((candidate) => candidate.normalized === normalized) ||
                  diseasePool.find(
                    (candidate) =>
                      candidate.normalized.includes(normalized) ||
                      normalized.includes(candidate.normalized)
                  );
                const matchedServiceId = serviceMapsRows.find(
                  (mapItem) => Number(mapItem.subGroupId) === Number(item.subGroupId)
                )?.serviceId;
                if (matched) {
                  return {
                    id: Number(matched.id),
                    name: matched.name,
                    serviceId: Number(matchedServiceId) > 0 ? Number(matchedServiceId) : null,
                  };
                }
                return {
                  id: SYNTHETIC_INDEX_OFFSET + Number(item.subGroupId),
                  name: item.name,
                  serviceId: Number(matchedServiceId) > 0 ? Number(matchedServiceId) : null,
                };
              })
              .filter((item) => Boolean(item?.name))
              .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ru"));
          }
        }

        if (!indicators.length) {
          indicators = (diseaseIndices || [])
            .map((item) => ({
              id: Number(item.id),
              name: item.name || `Индекс ${item.id}`,
              serviceId: null,
            }))
            .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ru"));
        }

        updateIndicatorGroup(groupKey, { loading: false, indicators });
      } catch (error) {
        updateIndicatorGroup(groupKey, { loading: false });
        alert.show(0, error);
      }
    },
    [alert, diseaseIndices, indicatorGroups, updateIndicatorGroup]
  );

  const isGroupIndicatorInPlan = useCallback(
    (group, item) =>
      planIndices.some(
        (row) =>
          !row._isDeleted && rowMatchesPlanGroupIndicator(row, group, item)
      ),
    [planIndices]
  );

  const toggleGroupIndicator = useCallback(
    (groupKey, item, checked) => {
      const group = indicatorGroups.find((g) => g.key === groupKey);
      if (!group) return;
      if (checked) {
        addIndicatorsToPlan([item], {
          departmentId: group.departmentId,
          departmentName: group.departmentName,
          groupId: group.groupId,
          groupName: group.groupName,
        });
      } else {
        removePlanRowForGroupIndicator(group, item);
      }
    },
    [indicatorGroups, addIndicatorsToPlan, removePlanRowForGroupIndicator]
  );

  const setSelectedForAllInGroup = useCallback(
    (groupKey, checked) => {
      const group = indicatorGroups.find((item) => item.key === groupKey);
      if (!group) return;
      const list = group.indicators || [];
      if (checked) {
        addIndicatorsToPlan(list, {
          departmentId: group.departmentId,
          departmentName: group.departmentName,
          groupId: group.groupId,
          groupName: group.groupName,
        });
      } else {
        removePlanRowsForGroupIndicatorList(group, list);
      }
    },
    [indicatorGroups, addIndicatorsToPlan, removePlanRowsForGroupIndicatorList]
  );

  const removePlanIndex = (idx) => {
    setPlanIndices((prev) => prev.filter((_, itemIdx) => itemIdx !== idx));
  };

  /** Количество проб на уровне лаборатории/группы — пишется в plannedPlan всех строк секции. */
  const updateSectionSampleCount = useCallback((groupKey, value) => {
    const numValue = value === "" || value == null ? 1 : Number(value);
    setPlanIndices((prev) =>
      prev.map((row) =>
        !row._isDeleted && planIndexSectionKey(row) === groupKey
          ? { ...row, plannedPlan: Number.isFinite(numValue) ? numValue : 1 }
          : row
      )
    );
  }, []);

  return (
    <DashboardLayout>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Grid item xs={12}>
            <Paper>
              <Toolbar
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h5" component="h2">
                  Создание плана
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <NavLink style={{ color: "#2f9e69" }} to={backRoute}>
                    <span style={{ color: "2f9e69" }}>Вернуться к плану</span>
                  </NavLink>
                </Stack>
              </Toolbar>
            </Paper>
          </Grid>

          <Grid item xs={12} spacing={1} style={{ paddingTop: 16 }}>
            <Paper style={{ paddingBottom: 20 }}>
              <Grid container xs={12} style={{ paddingLeft: 20, paddingRight: 20 }}>
                <Grid item xs={12} marginTop={2}>
                  <Typography variant="h6">Объект</Typography>
                </Grid>

                <Grid item xs={12} style={{ paddingTop: 8 }}>
                  <AnObjectField
                    disabled={review}
                    label="Выбор объекта"
                    value={fields.targetObject}
                    onChange={(obj) => {
                      setFields((state) => ({
                        ...state,
                        targetObject: obj,
                        inspectedEntityName: obj?.name || "",
                        individualOrBusinessId: obj?.bin || "",
                        inspectedObjectName: obj?.name || "",
                        location: obj?.addressLine || "",
                      }));
                    }}
                  />
                </Grid>

                <Grid item xs={12} marginTop={3}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Индексы плана</Typography>
                    {!review && (
                      <LoadingButton variant="outlined" onClick={addIndicatorGroup}>
                        Добавить группу
                      </LoadingButton>
                    )}
                  </Stack>
                </Grid>

                {!review &&
                  <Grid item xs={12} style={{ paddingTop: 8 }}>
                    {(indicatorGroups || []).map((group, groupIndex) => (
                      <div
                        key={group.key}
                        style={{
                          border: "1px solid #d9d9d9",
                          borderRadius: 8,
                          padding: 12,
                          marginBottom: 12,
                        }}
                      >
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={5}>
                            <RestAutoComplete
                              restApi={departmentCRUD}
                              id={`department_${group.key}`}
                              label="Лаборатория"
                              displayEndAdornment={false}
                              value={group.departmentId}
                              query={{
                                paging: { skip: 0, take: 200 },
                                sort: { name: { operator: "asc" } },
                                filter: { isDeleted: { operand1: false, operator: "equals" } },
                              }}
                              onChange={(obj) =>
                                updateIndicatorGroup(group.key, {
                                  departmentId: obj?.id || null,
                                  departmentName: obj?.name ?? null,
                                  groupId: null,
                                  groupName: null,
                                  indicators: [],
                                })
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={5}>
                            <RestAutoComplete
                              restApi={serviceGroupCRUD}
                              id={`group_${group.key}`}
                              label="Группа"
                              displayEndAdornment={false}
                              value={group.groupId}
                              disabled={!group.departmentId}
                              query={{
                                paging: { skip: 0, take: 200 },
                                sort: { name: { operator: "asc" } },
                              }}
                              onSearch={async (request, getFunc) => {
                                const response = await getFunc();
                                if (!group.departmentId) return response;

                                const mapResponse = await serviceMapCRUD.search({
                                  paging: { skip: 0, take: 10000, returnCount: false },
                                  filter: {
                                    departmentId: {
                                      operand1: String(group.departmentId),
                                      operator: "equals",
                                    },
                                  },
                                });
                                const allowedGroupIds = new Set(
                                  pickRows(mapResponse).map((item) => Number(item.groupId))
                                );
                                return {
                                  ...response,
                                  result: (response?.result || []).filter((item) =>
                                    allowedGroupIds.has(Number(item.id))
                                  ),
                                };
                              }}
                              onChange={(obj) =>
                                updateIndicatorGroup(group.key, {
                                  groupId: obj?.id || null,
                                  groupName: obj?.name ?? null,
                                  indicators: [],
                                })
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={2} style={{ display: "flex", alignItems: "center" }}>
                            <LoadingButton
                              fullWidth
                              variant="contained"
                              loading={group.loading}
                              onClick={() => loadGroupIndicators(group.key)}
                            >
                              Поиск
                            </LoadingButton>
                          </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} style={{ marginTop: 8 }}>
                          <LoadingButton
                            size="small"
                            variant="contained"
                            onClick={() => setSelectedForAllInGroup(group.key, true)}
                            disabled={!(group.indicators || []).length}
                          >
                            Выбрать все
                          </LoadingButton>
                          <LoadingButton
                            size="small"
                            variant="contained"
                            onClick={() => setSelectedForAllInGroup(group.key, false)}
                            disabled={
                              !(group.indicators || []).some((ind) =>
                                isGroupIndicatorInPlan(group, ind)
                              )
                            }
                          >
                            Снять выбор со всех
                          </LoadingButton>
                        </Stack>

                        <div
                          style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: 4,
                            marginTop: 8,
                            padding: 8,
                          }}
                        >
                          <Table>
                            <TableBody>
                              {(group.indicators || []).map((item) => (
                                <TableRow key={`${group.key}_${item.id}`}>
                                  <TableCell variant="head" style={{ fontWeight: "bold" }}>
                                    {item.name}
                                  </TableCell>
                                  <TableCell align="right" width={56}>
                                    <Checkbox
                                      checked={isGroupIndicatorInPlan(group, item)}
                                      onChange={(event) =>
                                        toggleGroupIndicator(
                                          group.key,
                                          item,
                                          event.target.checked
                                        )
                                      }
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        <Stack direction="row" justifyContent="flex-end" style={{ marginTop: 8 }}>
                          {(indicatorGroups.length > 1 || groupIndex > 0) && (
                            <IconButton size="small" onClick={() => removeIndicatorGroup(group.key)}>
                              <DeleteOutlineIcon />
                            </IconButton>
                          )}
                        </Stack>
                      </div>
                    ))}
                  </Grid>}

                <Grid item xs={12} style={{ paddingTop: 8 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Индикатор</TableCell>
                        <TableCell>План (иссл.)</TableCell>
                        {!review && <TableCell />}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {planIndexTableGroups.map((section) => {
                        const sampleCount =
                          section.entries[0]?.row?.plannedPlan != null &&
                          section.entries[0].row.plannedPlan !== ""
                            ? section.entries[0].row.plannedPlan
                            : 1;
                        const headerColSpan = review ? 2 : 3;
                        return (
                        <React.Fragment key={section.groupKey}>
                          <TableRow>
                            <TableCell
                              colSpan={headerColSpan}
                              sx={{
                                bgcolor: "action.hover",
                                fontWeight: 600,
                                borderBottom: "2px solid",
                                borderColor: "divider",
                                py: 1.25,
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={2}
                                flexWrap="wrap"
                              >
                                <span>{section.departmentLabel}</span>
                                <TextField
                                  label="Количество проб"
                                  type="number"
                                  size="small"
                                  disabled={review}
                                  value={sampleCount}
                                  onChange={(event) =>
                                    updateSectionSampleCount(
                                      section.groupKey,
                                      event.target.value
                                    )
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">шт</InputAdornment>
                                    ),
                                  }}
                                  inputProps={{ min: 0, step: 1 }}
                                  sx={{ width: 200, bgcolor: "background.paper" }}
                                />
                              </Stack>
                            </TableCell>
                          </TableRow>
                          {section.entries.map(({ row, fullIdx }) => (
                            <TableRow key={row.id || `new_${fullIdx}`}>
                              <TableCell>
                                {row.indexName || `Индекс ${row.indexId}`}
                              </TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="number"
                                  disabled={review}
                                  value={row.plannedResearch}
                                  onChange={(event) =>
                                    updatePlanIndexField(
                                      fullIdx,
                                      "plannedResearch",
                                      event.target.value
                                    )
                                  }
                                />
                              </TableCell>
                              {!review && (
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => removePlanIndex(fullIdx)}
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {!review && (
            <LoadingButton
              style={{ marginTop: "10px" }}
              loading={loadingSubmit}
              variant="contained"
              onClick={submit}
            >
              Сохранить
            </LoadingButton>
          )}

          {alert.render()}
        </>
      )}
    </DashboardLayout>
  );
};

export default PlanDetailForm;